import type {
  AberturaDeComanda,
  ComandaAberta,
  ComandaFechada,
  ConsultaDeComandas,
  EntradaDeMercadoria,
  EntradaRegistrada,
  FechamentoDeComanda,
  Inventario,
  InventarioFechado,
  LancamentoDeItem,
  PaginaDeComandas,
  PaginaDeEntradas,
  PaginaDeInventarios,
  PaginaDeMovimentos,
  PainelDoBar,
  ProdutoDetalhado,
  ProdutoEdicao,
  ProdutoNaTabela,
  ProdutoNovo,
  ProdutoParaComanda,
  ProdutoParaRepor,
} from '@arena/shared/schemas';
import { api } from './cliente.js';

/**
 * As chamadas do bar — `#v-bar`, `#m-comanda`, `#v-estoque`,
 * `#m-produto`, `#m-inventario` e a aba Comandas de `#m-aluno`.
 *
 * Dois recursos da matriz vivem aqui, e as rotas refletem isso: o que
 * exige `estoque` está sob `/estoque`, o que exige `comanda` está sob
 * `/comandas`. O Professor, que tem comanda e não tem estoque, recebe
 * 403 no primeiro grupo e 200 no segundo — e é por isso que a tela do
 * bar não pode depender de nenhuma chamada de `/estoque` para funcionar.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export const estoqueApi = {
  // --- produtos ---
  produtos(incluirInativos = false): Promise<ProdutoNaTabela[]> {
    return api<ProdutoNaTabela[]>(`/estoque/produtos${incluirInativos ? '?inativos=true' : ''}`);
  },

  produto(id: string): Promise<ProdutoDetalhado> {
    return api<ProdutoDetalhado>(`/estoque/produtos/${id}`);
  },

  criarProduto(dados: ProdutoNovo): Promise<ProdutoNaTabela> {
    return api<ProdutoNaTabela>('/estoque/produtos', { metodo: 'POST', corpo: dados });
  },

  editarProduto(id: string, dados: ProdutoEdicao): Promise<{ id: string }> {
    return api<{ id: string }>(`/estoque/produtos/${id}`, { metodo: 'PATCH', corpo: dados });
  },

  reposicao(): Promise<ProdutoParaRepor[]> {
    return api<ProdutoParaRepor[]>('/estoque/reposicao');
  },

  // --- compra ---
  fornecedores(): Promise<{ id: string; nome: string }[]> {
    return api<{ id: string; nome: string }[]>('/estoque/fornecedores');
  },

  criarFornecedor(dados: { nome: string; contato?: string }): Promise<{ id: string; nome: string }> {
    return api<{ id: string; nome: string }>('/estoque/fornecedores', {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /**
   * Uma compra inteira numa chamada só: vários produtos, cadastrados ou
   * novos. O produto novo nasce no servidor, na mesma transação — a tela
   * não cadastra produto à parte.
   */
  registrarEntrada(dados: EntradaDeMercadoria): Promise<EntradaRegistrada> {
    return api<EntradaRegistrada>('/estoque/entradas', { metodo: 'POST', corpo: dados });
  },

  /** O histórico de compras: uma página por vez, da mais recente para trás. */
  entradas(consulta: { cursor?: string; limite: number }): Promise<PaginaDeEntradas> {
    const query = new URLSearchParams({ limite: String(consulta.limite) });
    if (consulta.cursor) query.set('cursor', consulta.cursor);
    return api<PaginaDeEntradas>(`/estoque/entradas?${query.toString()}`);
  },

  /** O histórico de um produto: uma página por vez, do mais recente para trás. */
  movimentosDoProduto(
    id: string,
    consulta: { cursor?: string; limite: number },
  ): Promise<PaginaDeMovimentos> {
    const query = new URLSearchParams({ limite: String(consulta.limite) });
    if (consulta.cursor) query.set('cursor', consulta.cursor);
    return api<PaginaDeMovimentos>(`/estoque/produtos/${id}/movimentos?${query.toString()}`);
  },

  /** O histórico de contagens: uma página por vez, da mais recente para trás. */
  inventarios(consulta: { cursor?: string; limite: number }): Promise<PaginaDeInventarios> {
    const query = new URLSearchParams({ limite: String(consulta.limite) });
    if (consulta.cursor) query.set('cursor', consulta.cursor);
    return api<PaginaDeInventarios>(`/estoque/inventarios?${query.toString()}`);
  },

  // --- contagem ---
  contagem(): Promise<ProdutoNaTabela[]> {
    return api<ProdutoNaTabela[]>('/estoque/contagem');
  },

  fecharInventario(dados: Inventario): Promise<InventarioFechado> {
    return api<InventarioFechado>('/estoque/inventarios', { metodo: 'POST', corpo: dados });
  },

  // --- comandas ---
  comandasAbertas(): Promise<ComandaAberta[]> {
    return api<ComandaAberta[]>('/comandas/abertas');
  },

  /** O histórico: fechadas ou canceladas, uma página por vez, com os totais por forma. */
  historicoDeComandas(consulta: ConsultaDeComandas): Promise<PaginaDeComandas> {
    const query = new URLSearchParams({
      situacao: consulta.situacao,
      limite: String(consulta.limite),
    });
    for (const campo of ['de', 'ate', 'forma', 'cursor'] as const) {
      const valor = consulta[campo];
      if (valor) query.set(campo, valor);
    }
    return api<PaginaDeComandas>(`/comandas?${query.toString()}`);
  },

  comanda(id: string): Promise<ComandaAberta> {
    return api<ComandaAberta>(`/comandas/${id}`);
  },

  comandasDoAluno(alunoId: string): Promise<ComandaAberta[]> {
    return api<ComandaAberta[]>(`/comandas/do-aluno/${alunoId}`);
  },

  /**
   * A sugestão de `#m-comanda`, chamada a cada tecla.
   *
   * Vai para `/comandas/produtos/busca`, que exige `comanda:lancar` — a
   * ação de quem escolhe o produto — e devolve só nome, preço e saldo.
   * A de `/estoque` exige `estoque:ler`, que o Professor não tem.
   */
  buscarProdutoDaComanda(termo: string, limite = 8): Promise<ProdutoParaComanda[]> {
    const query = new URLSearchParams({ termo, limite: String(limite) });
    return api<ProdutoParaComanda[]>(`/comandas/produtos/busca?${query.toString()}`);
  },

  painel(dias = 7): Promise<PainelDoBar> {
    return api<PainelDoBar>(`/comandas/painel?dias=${dias}`);
  },

  abrirComanda(dados: AberturaDeComanda): Promise<ComandaAberta> {
    return api<ComandaAberta>('/comandas', { metodo: 'POST', corpo: dados });
  },

  lancarItem(
    comandaId: string,
    dados: LancamentoDeItem,
  ): Promise<{ itemId: string; total: number }> {
    return api<{ itemId: string; total: number }>(`/comandas/${comandaId}/itens`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  /** Zero remove a linha — é o "menos" de `#m-comanda`. */
  ajustarItem(
    comandaId: string,
    itemId: string,
    quantidade: number,
  ): Promise<{ removido: boolean }> {
    return api<{ removido: boolean }>(`/comandas/${comandaId}/itens/${itemId}`, {
      metodo: 'PATCH',
      corpo: { quantidade },
    });
  },

  fecharComanda(comandaId: string, dados: FechamentoDeComanda): Promise<ComandaFechada> {
    return api<ComandaFechada>(`/comandas/${comandaId}/fechamento`, {
      metodo: 'POST',
      corpo: dados,
    });
  },

  cancelarComanda(comandaId: string, motivo: string): Promise<{ id: string }> {
    return api<{ id: string }>(`/comandas/${comandaId}/cancelamento`, {
      metodo: 'POST',
      corpo: { motivo },
    });
  },

  /** O estorno do fechamento: estoque de volta, dinheiro desfeito, comanda reaberta. */
  estornarComanda(comandaId: string, motivo: string): Promise<{ id: string }> {
    return api<{ id: string }>(`/comandas/${comandaId}/estorno`, {
      metodo: 'POST',
      corpo: { motivo },
    });
  },

  /** O estorno de uma entrada: estoque e custo de volta, dinheiro desfeito. */
  estornarEntrada(entradaId: string, motivo: string): Promise<{ id: string }> {
    return api<{ id: string }>(`/estoque/entradas/${entradaId}/estorno`, {
      metodo: 'POST',
      corpo: { motivo },
    });
  },
};
